<template>
  <div>
    <base-overlay :show="fetchingOrders">
      <b-card>
        <b-container fluid>
          <b-row class="mb-2">
            <b-col
              md="4"
            >
              <div>

                <label for="states">Search by state</label>
                <v-select
                  id="states"
                  v-model="queryParams.state"
                  placeholder="No state selected"
                  label="label"
                  :options="stateOptions"
                  :reduce="(option) => option.key"
                  :clearable="false"
                />
              </div>
            </b-col>
            <b-col md="4">
              <label for="business-id">Search by business id</label>
              <b-form-input
                id="business-id"
                v-model="queryParams.businessId"
                placeholder="Business ID"
              />
            </b-col>
            <b-col md="4">
              <label for="branch-id">Search by branch id</label>
              <b-form-input
                id="branch-id"
                v-model="queryParams.branchId"
                placeholder="Branch ID"
              />
            </b-col>
            <b-col
              md="4"
            >
              <label for="date1">From Date</label>
              <b-form-datepicker
                id="date1"
                v-model="dateTimeFilter.fromDate"
                :min="disableDateAt()"
              />
              <label
                class="mt-1"
              >
                From Time</label>
              <b-input-group>
                <flat-pickr
                  v-model="dateTimeFilter.fromTime"
                  class="form-control"
                  :config="{ enableTime: true, noCalendar: true, time_24hr: true}"
                />
                <b-input-group-append>
                  <b-button
                    v-if="dateTimeFilter.fromTime"
                    class="btn-icon"
                    variant="primary"
                    @click="dateTimeFilter.fromTime = ''"
                  >
                    <feather-icon icon="XIcon" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-col>
            <b-col
              md="4"
            >
              <label for="date2">To Date</label>
              <b-form-datepicker
                id="date2"
                v-model="dateTimeFilter.toDate"
                :min="disableDateAt()"
              />
              <label
                class="mt-1"
                for="time2"
              >To Time</label>
              <b-input-group>
                <flat-pickr
                  v-model="dateTimeFilter.toTime"
                  class="form-control"
                  :config="{ enableTime: true, noCalendar: true, time_24hr: true}"
                />
                <b-input-group-append>
                  <b-button
                    v-if="dateTimeFilter.toTime"
                    class="btn-icon"
                    variant="primary"
                    @click="dateTimeFilter.toTime = ''"
                  >
                    <feather-icon icon="XIcon" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-col>
            <b-col md="4">
              <div
                class="mt-auto h-full d-flex  align-items-end justify-content-end"
                style="height:100%"
              >
                <b-button
                  variant="primary"
                  @click="fetchOrders"
                >
                  Search
                </b-button>
                <b-button
                  variant="outline-primary"
                  class="mx-1"
                  @click="resetFilter"
                >
                  Clear
                </b-button>
                <b-button
                  class="btn-icon rounded-circle"
                  variant="flat-primary"
                  @click="fetchOrders"
                >
                  <feather-icon
                    icon="RefreshCwIcon"
                  />
                </b-button>
                <!-- <h6 class="m-0">
                    Total: {{ totalTrips }}
                  </h6> -->
              </div>
            </b-col>
          </b-row>
        </b-container>
        <b-table
          hover
          striped
          selectable
          select-mode="single"
          :items="ordersList"
          :fields="tableColumns"
          show-empty
          class="text-center"
          @row-selected="navigateToOrderDetails($event[0].id)"
        />
        <base-pagination
          v-model="queryParams.page"
          :total="totalOrders"
          @input="fetchOrders"
          @new-per-page="queryParams.perPage = $event"
        />
      </b-card>
    </base-overlay>
  </div>
</template>

<script>
import {
  BCard, BTable, BContainer, BRow, BCol, BButton, BFormInput, BFormDatepicker, BInputGroupAppend, BInputGroup,
} from 'bootstrap-vue'
import { reactive, ref } from '@vue/composition-api'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import BasePagination from '@/UI/BasePagination.vue'
import BaseOverlay from '@/UI/BaseOverlay.vue'
import axiosIns from '@/libs/axios'
import snakeCaseToCamelCase from '@/utils/snake-case-to-camcel-caase'
import formatDate from '@/utils/date-format'
import { errorNotification } from '@/utils/toast-notifications'

export default {
  components: {
    BCard,
    BTable,
    BInputGroup,
    BasePagination,
    BaseOverlay,
    BInputGroupAppend,
    flatPickr,
    BFormDatepicker,
    BContainer,
    BFormInput,
    BRow,
    BCol,
    BButton,
    vSelect,
  },

  setup() {
    const disableDateAt = () => {
      const now = new Date()
      now.setMonth(now.getMonth() - 1)
      now.setHours(0, 0, 0, 0)
      return now
    }
    const tableColumns = [
      {
        label: 'Business',
        key: 'business',
        formatter: val => val?.name?.en || '',
      },
      {
        label: 'Branch',
        key: 'branch',
        formatter: val => val?.name?.en || '',
      },
      {
        label: 'State',
        key: 'state',
        formatter: val => snakeCaseToCamelCase(val),
      },
      {
        label: 'Total Price',
        key: 'totalPrice',
      },
      {
        label: 'Created At',
        key: 'createdAt',
        formatter: val => formatDate(val),
      },
    ]
    const stateOptions = [
      {
        label: 'All',
        key: 'ALL',
      },
      {
        label: 'Created',
        key: 'CREATED',
      },
      {
        label: 'Canceled',
        key: 'CANCELED',
      },
      {
        label: 'Accepted',
        key: 'ACCEPTED',
      },
      {
        label: 'Declined',
        key: 'DECLINED',
      },
      {
        label: 'Preparation',
        key: 'PREPARATION',
      },
      {
        label: 'Waiting For Driver',
        key: 'WAITING_DRIVER',
      },
      {
        label: 'Out For Delivery',
        key: 'OUT_FOR_DELIVERY',
      },
      {
        label: 'Completed',
        key: 'COMPLETED',
      },
    ]
    const ordersList = ref([])
    const totalOrders = ref(0)
    const initialQueryParams = () => ({
      perPage: 10,
      page: 1,
      state: 'ALL',
      from: undefined,
      to: undefined,
      branchId: undefined,
      businessId: undefined,
    }
    )
    const queryParams = reactive(initialQueryParams())

    const fetchingOrders = ref(false)

    const tempTime = new Date()
    const dateTimeFilter = reactive({
      fromDate: `${tempTime.getFullYear()}-${tempTime.getMonth() + 1}-${tempTime.getDate()}`,
      fromTime: undefined,
      toDate: `${tempTime.getFullYear()}-${tempTime.getMonth() + 1}-${tempTime.getDate()}`,
      toTime: undefined,
    })
    const setFilterDateFormat = () => {
      if (dateTimeFilter.fromDate) {
        if (!dateTimeFilter.fromTime) {
          queryParams.from = new Date(`${dateTimeFilter.fromDate} 00:00:00`)
        } else {
          queryParams.from = new Date(`${dateTimeFilter.fromDate} ${dateTimeFilter.fromTime}`)
        }
      }
      if (dateTimeFilter.toDate) {
        if (dateTimeFilter.fromDate) {
          if (!dateTimeFilter.toTime) {
            const now = new Date()
            queryParams.to = new Date(`${dateTimeFilter.toDate} ${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}`)
          } else {
            queryParams.to = new Date(`${dateTimeFilter.toDate} ${dateTimeFilter.toTime}`)
          }
          return true
        }
        errorNotification('You should fill the from date and time first')
        return false
      }
      return true
    }
    const fetchOrders = async () => {
      const val = setFilterDateFormat()
      if (!val) return
      const params = {
        ...queryParams,
      }
      if (queryParams.state === 'ALL') params.state = undefined
      try {
        fetchingOrders.value = true
        const res = await axiosIns.get('/orders', {
          params,
        })
        const { orders, total } = res.data.data
        ordersList.value = orders.map(o => ({ ...o, branch: o.business.branch }))
        totalOrders.value = total
      } finally {
        fetchingOrders.value = false
      }
    }
    const resetFilter = () => {
      dateTimeFilter.fromDate = undefined
      dateTimeFilter.toDate = undefined
      dateTimeFilter.fromTime = undefined
      dateTimeFilter.toTime = undefined
      const newFilter = initialQueryParams()
      Object.keys(newFilter).forEach(key => {
        queryParams[key] = newFilter[key]
      })
      fetchOrders()
    }

    const navigateToOrderDetails = id => window.open(`${window.location.origin}/businesses/orders/${id}`)

    fetchOrders()

    return {
      ordersList,
      resetFilter,
      navigateToOrderDetails,
      queryParams,
      fetchOrders,
      stateOptions,
      totalOrders,
      tableColumns,
      disableDateAt,
      fetchingOrders,
      dateTimeFilter,
    }
  },
}
</script>

<style>

</style>
